import { observer } from 'mobx-react-lite'
import { EditPencil, NavArrowDown, NavArrowRight, Trash } from 'iconoir-react'
import { BuildingOfficeIcon, UserIcon } from '@phosphor-icons/react'
import { Fragment, ReactElement, ReactNode, useMemo, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { TableBody, TableCell, TableRow } from 'app/components/ui/table'
import { FeatureToggleJson } from 'app/types/entities'
import { AddOverrideModal } from './AddOverrideModal'
import { OverrideName } from './OverrideName'
import { ToggleLabelCell } from './ToggleCells'
import type { FeatureRow, TargetRef } from './featureToggleModel'
import type { useToggleTableState } from './useToggleTableState'

type Segment = `off` | `everyone` | `specific`

type Props = {
  rows: FeatureRow[]
  rowState: ReturnType<typeof useToggleTableState>
  onToggleGlobal: (row: FeatureRow, next: boolean) => void | Promise<void>
  onSetOff: (row: FeatureRow) => void | Promise<void>
  onSetSpecific: (row: FeatureRow) => void | Promise<void>
  onAddOverrides?: (name: string, kind: TargetRef[`kind`], ids: string[]) => void | Promise<void>
  onRemoveOverride: (record: FeatureToggleJson) => void | Promise<void>
  onEditToggle?: (row: FeatureRow) => void
  onSetArchived?: (row: FeatureRow, archived: boolean) => void | Promise<void>
  rowNote?: (row: FeatureRow) => ReactNode
  isRowHighlighted?: (row: FeatureRow) => boolean
}

// Company/user icon counts shown next to the control when a toggle is on for a
// specific subset.
function OverrideCounts({ row }: { row: FeatureRow }) {
  const ws = row.workspaceOverrides.length
  const us = row.userOverrides.length
  if (ws + us === 0) return null
  return (
    <span className="inline-flex items-center gap-2.5 text-xs text-subtle">
      {ws > 0 && (
        <span className="inline-flex items-center gap-1" title={`${ws} workspace${ws > 1 ? `s` : ``}`}>
          <BuildingOfficeIcon className="h-3.5 w-3.5 text-minimal" />
          {ws}
        </span>
      )}
      {us > 0 && (
        <span className="inline-flex items-center gap-1" title={`${us} user${us > 1 ? `s` : ``}`}>
          <UserIcon className="h-3.5 w-3.5 text-minimal" />
          {us}
        </span>
      )}
    </span>
  )
}

const SEGMENTS: Array<{ key: Segment; label: string }> = [
  { key: `off`, label: `Off` },
  { key: `everyone`, label: `Everyone` },
  { key: `specific`, label: `Specific` },
]

// The three mutually-exclusive enablement states as an inline segmented control.
function EnablementControl({
  selected,
  onSelect,
  disabled,
}: {
  selected: Segment
  onSelect: (seg: Segment) => void
  disabled?: boolean
}) {
  return (
    <div className="inline-flex rounded-md border border-default bg-surface p-0.5" onClick={(e) => e.stopPropagation()}>
      {SEGMENTS.map((s) => (
        <Button
          key={s.key}
          variant="segment"
          size="xs"
          aria-pressed={selected === s.key}
          disabled={disabled}
          onClick={() => onSelect(s.key)}
        >
          {s.label}
        </Button>
      ))}
    </div>
  )
}

function OverrideSection({
  row,
  kind,
  label,
  records,
  onAddOverrides,
  onRemoveOverride,
}: {
  row: FeatureRow
  kind: TargetRef[`kind`]
  label: string
  records: FeatureToggleJson[]
  onAddOverrides?: Props[`onAddOverrides`]
  onRemoveOverride: Props[`onRemoveOverride`]
}) {
  const readOnly = row.archived
  const [addOpen, setAddOpen] = useState(false)
  const existingIds = useMemo(() => new Set(records.map((r) => r.typeId).filter((id): id is string => !!id)), [records])
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-subtle">
          {kind === `Workspace` ? <BuildingOfficeIcon className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
          {label}
          {records.length > 0 && <span className="font-normal tabular-nums text-minimal">{records.length}</span>}
        </span>
        {!readOnly && (
          <Button size="sm" variant="tertiary" onClick={() => setAddOpen(true)}>
            Add
          </Button>
        )}
        <AddOverrideModal
          open={addOpen}
          onOpenChange={setAddOpen}
          kind={kind}
          existingIds={existingIds}
          onConfirm={(ids) => onAddOverrides?.(row.name, kind, ids)}
        />
      </div>
      <div className="overflow-hidden rounded-md border border-default bg-surface">
        {records.length === 0 ? (
          <p className="px-3 py-2 text-xs text-minimal">None</p>
        ) : (
          <ul className="divide-y divide-subtle">
            {records.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-default">
                <OverrideName kind={kind} id={r.typeId} />
                {!readOnly && (
                  <Button
                    variant="subtle"
                    size="icon-sm"
                    aria-label="Remove override"
                    onClick={() => onRemoveOverride(r)}
                  >
                    <Trash />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function ExpandedPanel({
  row,
  segment,
  onAddOverrides,
  onRemoveOverride,
  onEditToggle,
  onSetArchived,
}: {
  row: FeatureRow
  segment: Segment
  onAddOverrides?: Props[`onAddOverrides`]
  onRemoveOverride: Props[`onRemoveOverride`]
  onEditToggle?: Props[`onEditToggle`]
  onSetArchived?: Props[`onSetArchived`]
}) {
  return (
    <div className="space-y-4 bg-surface-subtle/60 px-5 py-4">
      <div className="flex items-center gap-2">
        {onEditToggle && !row.archived && (
          <Button variant="subtle" size="xs" onClick={() => onEditToggle(row)}>
            <EditPencil />
            Edit details
          </Button>
        )}
        {onSetArchived && (
          <Button size="xs" variant="tertiary" className="ml-auto" onClick={() => onSetArchived(row, !row.archived)}>
            {row.archived ? `Unarchive` : `Archive`}
          </Button>
        )}
      </div>
      {row.archived && <p className="text-xs text-subtle">Archived. Unarchive it to change who has it.</p>}
      {segment === `specific` ? (
        <>
          <OverrideSection
            row={row}
            kind="Workspace"
            label="Workspace overrides"
            records={row.workspaceOverrides}
            onAddOverrides={onAddOverrides}
            onRemoveOverride={onRemoveOverride}
          />
          <OverrideSection
            row={row}
            kind="User"
            label="User overrides"
            records={row.userOverrides}
            onAddOverrides={onAddOverrides}
            onRemoveOverride={onRemoveOverride}
          />
        </>
      ) : row.archived ? null : (
        <p className="text-xs text-subtle">
          {segment === `everyone`
            ? `On for everyone. Choose "Specific" to target individual workspaces or users instead.`
            : `Off — choose "Everyone" or "Specific" to enable this toggle.`}
        </p>
      )}
    </div>
  )
}

// The toggle rows shared by the By feature and By release views: label, the
// Off / Everyone / Specific control, and the expandable override lists.
export const FeatureToggleTable = observer(function FeatureToggleTable({
  rows,
  rowState,
  onToggleGlobal,
  onSetOff,
  onSetSpecific,
  onAddOverrides,
  onRemoveOverride,
  onEditToggle,
  onSetArchived,
  rowNote,
  isRowHighlighted,
}: Props): ReactElement {
  const { expanded, setExpanded, specificMode, setSpecificMode } = rowState

  const toggleExpand = (name: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })

  const segmentOf = (row: FeatureRow): Segment => {
    if (row.state === `global`) return `everyone`
    if (row.state === `overrides`) return `specific`
    return specificMode.has(row.name) ? `specific` : `off`
  }

  const setSegment = (row: FeatureRow, seg: Segment) => {
    setSpecificMode((prev) => {
      const next = new Set(prev)
      if (seg === `specific`) next.add(row.name)
      else next.delete(row.name)
      return next
    })
    if (seg === `everyone`) {
      onToggleGlobal(row, true)
    } else if (seg === `off`) {
      onSetOff(row)
    } else {
      setExpanded((prev) => new Set(prev).add(row.name))
      onSetSpecific(row)
    }
  }

  return (
    <div className="overflow-hidden rounded-md border border-default bg-surface shadow-elevation-1">
      <table className="w-full caption-bottom table-fixed text-sm">
        <colgroup>
          <col />
          <col className="w-72" />
          <col className="w-12" />
        </colgroup>
        <TableBody className="divide-y divide-subtle">
          {rows.map((row) => {
            const seg = segmentOf(row)
            return (
              <Fragment key={row.name}>
                <TableRow
                  aria-expanded={expanded.has(row.name)}
                  className="cursor-pointer"
                  data-state={isRowHighlighted?.(row) ? `warning` : undefined}
                  onClick={() => toggleExpand(row.name)}
                >
                  <TableCell className="max-w-0 whitespace-normal px-4 py-3 align-middle">
                    <ToggleLabelCell label={row.label} slug={row.name} description={row.description} />
                    {rowNote?.(row)}
                  </TableCell>
                  <TableCell className="px-4 py-3 align-middle">
                    <div className="flex items-center gap-3">
                      <EnablementControl selected={seg} onSelect={(s) => setSegment(row, s)} disabled={row.archived} />
                      {seg === `specific` && <OverrideCounts row={row} />}
                    </div>
                  </TableCell>
                  <TableCell className="px-2 py-3 text-right align-middle">
                    <Button
                      variant="subtle"
                      size="icon-sm"
                      aria-label={`Expand ${row.label}`}
                      aria-expanded={expanded.has(row.name)}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleExpand(row.name)
                      }}
                    >
                      {expanded.has(row.name) ? <NavArrowDown /> : <NavArrowRight />}
                    </Button>
                  </TableCell>
                </TableRow>
                {expanded.has(row.name) && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={3} className="p-0">
                      <ExpandedPanel
                        row={row}
                        segment={seg}
                        onAddOverrides={onAddOverrides}
                        onRemoveOverride={onRemoveOverride}
                        onEditToggle={onEditToggle}
                        onSetArchived={onSetArchived}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </table>
    </div>
  )
})
